import type { DeadlineUrgency, Opportunity, PipelineStatus } from './types'

/**
 * Small shared formatting helpers, ported from the prototype's inline functions so every
 * view (and the report/CSV exports) sorts and labels opportunities identically.
 */

/** urgent → soon → watch, exactly as the prototype ranked deadline urgency. */
export const urgencyRank = (u: DeadlineUrgency | null | undefined): number =>
  u === 'urgent' ? 0 : u === 'soon' ? 1 : 2

/**
 * The one sort order the whole app uses: urgent deadlines first, then the model's fit
 * score descending. Returns a new array — callers keep their fetched order intact.
 */
export function sortOpps(opps: Opportunity[]): Opportunity[] {
  return [...opps].sort((a, b) => {
    const ua = urgencyRank(a.deadlineUrgency)
    const ub = urgencyRank(b.deadlineUrgency)
    if (ua !== ub) return ua - ub
    return (b.fitScore ?? 0) - (a.fitScore ?? 0)
  })
}

/** Relative date, as the prototype showed it: today / yesterday / 3d ago / 20 Jul. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000)
  if (diff === 0) return 'today'
  if (diff === 1) return 'yesterday'
  if (diff < 7) return `${diff}d ago`
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/** The pipeline status labels, unchanged from the prototype (trophy included). */
export const STATUS_LABEL: Record<PipelineStatus, string> = {
  new: 'New',
  prep: 'Bid prep',
  submitted: 'Submitted',
  won: 'Won 🏆',
  lost: 'Lost',
}

export const PIPELINE_STATUSES: { id: PipelineStatus; label: string }[] = [
  { id: 'new', label: 'New' },
  { id: 'prep', label: 'Bid prep' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'won', label: 'Won' },
  { id: 'lost', label: 'Lost' },
]

/** 'TBC'/'-'/empty deadlines are PIN-stage noise, not dates — the prototype treated them so. */
export const hasRealDeadline = (o: Opportunity): boolean =>
  !!o.deadline && o.deadline !== 'TBC' && o.deadline !== '-'
